import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useStore from "../../../../../hooks/useStore";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FORM_FIELDS,
  FREQUENCY_CONSTANTS,
  STATIC_GROUPS,
} from "../../../../../constants/constants";
import { Accordion, Form } from "react-bootstrap";
import FormDropdown from "../../../../../components/formDropdown";
import { useAuth } from "../../../../../context/authProvider";
import TableBody from "./tableBody";
import { generateAqiPayload } from "../../../../../utils/helpers";
import { toJS } from "mobx";
import { addGroupData } from "../../../../../helpers/groupDataCalculations";
import GlobalUnitDropDown from "../../../../../components/globalUnitDropDown";
import RecordExists from "../../../../../components/recordExists";

const AddAqiPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Defensive fallback for navigation state
  const {
    formData = undefined,
    groupsFilled = [],
    freq = {},
    formid = "",
    unitGlobal = ""
  } = location.state || {};

    // console.log("location.state:", location.state);
    // console.log("formData:", formData);
    // console.log("groupsFilled:", groupsFilled);
    // console.log("freq:", freq);
    // console.log("formid:", formid);
    // console.log("unitGlobal:", unitGlobal);
  const { showAlert } = useAuth();

  const {
    userStore: { userData },
    formStore: {
      fetchFields,
      fields,
      create,
      recordExists,
      formExists,
      setFormExists,
    },
    companyStore: { fetchUnits, companyUnit, kpiId, subCatId, catId, fetchKpiTarget },
  } = useStore();
  //states
  const [groupPayload, setGroupPayload] = useState([]);
  const [validate, setValidate] = useState(false);
  const [expanded, setExpanded] = useState("A");
  const [editForm, setEditForm] = useState("");
  const [globalUnit, setGlobalUnit] = useState(unitGlobal || "");
  const [frequency, setFrequency] = useState({
    ...FREQUENCY_CONSTANTS,
    ...freq,
  });

  // Always fetch fields and units on mount to ensure forms show
  useEffect(() => {
    fetchFields();
    fetchUnits();
  }, [fetchFields, fetchUnits]);
useEffect(() => {
    fetchKpiTarget({
        payloadData: {
        kpiId,
        subCatId,
        catId,
        year:
          frequency.frequencyYear ||
          String(new Date(frequency.frequencyDate).getFullYear()),
      }
    });
}, [catId, kpiId, subCatId, frequency.frequencyYear, frequency.frequencyDate, fetchKpiTarget]);

  const tempGroupPayload = useMemo(
    () => {
      try {
        // Always use fetched fields for rendering forms
        const fieldGroups = Object.entries(
          fields?.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.group?.[0]]: [
                ...(acc[curr.group?.[0]] ? acc[curr.group?.[0]] : []),
                { ...curr, ...FREQUENCY_CONSTANTS, ...FORM_FIELDS },
              ],
            }),
            {}
          ) || {}
        ).map(([key, value]) => ({
          group: key,
          value: Array.isArray(value) ? value.sort((a, b) => Number(a.position || 0) - Number(b.position || 0)) : [],
          label: STATIC_GROUPS?.aqi?.[key] || `Group ${key}`,
        }));
        return fieldGroups;
      } catch (error) {
        console.error("Error creating tempGroupPayload:", error);
        return [];
      }
    },
    [fields]
  );
  useEffect(() => {
    setGroupPayload(tempGroupPayload);
  }, [tempGroupPayload]);

  const allGroups = useMemo(
    () => Array.from(new Set(tempGroupPayload?.map(({ group }) => group || "").filter(Boolean))),
    [tempGroupPayload]
  );

  useEffect(() => {
    if (groupsFilled?.length > 0) {
      setExpanded(allGroups[groupsFilled?.length]);
    }
  }, [allGroups, groupsFilled]);

  const dropdownValidated = useMemo(() => {
    if (!frequency.frequencyType) return false;
    if (frequency.frequencyType === "Daily") {
      return Boolean(frequency.frequencyDate);
    } else if (frequency.frequencyType === "Yearly") {
      return Boolean(frequency.frequencyYear);
    } else if (frequency.frequencyType === "Monthly") {
      return Boolean(frequency.frequencyYear && frequency.frequencyMonth);
    }
    return false;
  }, [frequency]);

  const payload = useCallback(
    (data, fre) =>
      generateAqiPayload({
        data,
        companyUnit,
        kpiId,
        subCatId,
        catId,
        companyId: Number(toJS(userData)?.companyId),
      }).map((ele) => ({
        ...ele,
        ...fre,
      })),
    [catId, companyUnit, kpiId, subCatId, userData]
  );

  const generateGroupPayload = useCallback(
    (data) => {
      return Object.entries(
        data.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.group]: [...(acc[curr.group] || []), curr],
          }),
          []
        )
      ).map(([key, values]) => {
        // Calculate totals with NaN protection
        const targetValue = values.reduce((a, c) => {
          const val = Number(c.target);
          return a + (isNaN(val) || !isFinite(val) ? 0 : val);
        }, 0);
        
        const prevValue = values.reduce((a, c) => {
          const val = Number(c.prevValue);
          return a + (isNaN(val) || !isFinite(val) ? 0 : val);
        }, 0);
        
        const unitId = values.reduce((a, c) => {
          const val = Number(c.unit);
          return a + (isNaN(val) || !isFinite(val) ? 0 : val);
        }, 0);

        return {
          kpiId,
          subCatId,
          catId,
          targetValue: isNaN(targetValue) || !isFinite(targetValue) ? 0 : targetValue,
          groupName: key,
          prevValue: isNaN(prevValue) || !isFinite(prevValue) ? 0 : prevValue,
          companyUnit: companyUnit,
          unitId: isNaN(unitId) || !isFinite(unitId) ? 0 : unitId,
          ...frequency,
        };
      });
    },
    [catId, companyUnit, frequency, kpiId, subCatId]
  );

  const isDeviated = useMemo(() => {
    try {
      const g = addGroupData(
        groupPayload?.find((ele) => ele.group === "G")?.value || []
      );
      const i = addGroupData(
        groupPayload?.find((ele) => ele.group === "I")?.value || []
      );
      return i?.target !== g?.target || i?.prevValue !== g?.prevValue;
    } catch (error) {
      console.error("Error calculating isDeviated:", error);
      return false;
    }
  }, [groupPayload]);
  useEffect(() => {
    setGroupPayload((prev) =>
      prev?.map((ele) => ({
        ...ele,
        value: ele?.value?.map((e) => ({ ...e, ...frequency })),
      }))
    );
  }, [frequency]);

  useEffect(() => {
    if (dropdownValidated && !formid) {
      recordExists({ frequency, type: "aqi" });
    }
  }, [dropdownValidated, formid, frequency, recordExists]);

  useEffect(
    () => () => {
      setFormExists();
    },
    []
  );

  return (
    <div className="add-water-page">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Emission</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/company/aqi">Emission List</Link>
                </li>
                <li className="breadcrumb-item active">Emission</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card p-4">
        <Form validated={validate}>
          <FormDropdown
            frequency={frequency}
            setFrequency={setFrequency}
            isDisabled={groupsFilled?.length > 0}
          />
          <GlobalUnitDropDown
            setData={setGroupPayload}
            filterUnit={["Tonn Carbon di-oxide", "Metric tonn"]}
            global={globalUnit}
            setGlobal={setGlobalUnit}
            disabled={!!unitGlobal}
          />
        </Form>
        {formExists ? (
          <RecordExists />
        ) : (
          <Accordion
            className="water-table-accordion"
            defaultActiveKey={"A"}
            activeKey={expanded}
            onSelect={(eventKey) => {
              setExpanded(eventKey);
              setValidate(false);
            }}
          >
            <div className="table-responsive ">
              {groupPayload?.map((data, index) => (
                <Accordion.Item
                  key={`${data.group}-accordion-item`}
                  eventKey={data.group}
                >
                  <Accordion.Header>
                    <tr style={{ display: "flex", gap: "4px" }}>
                      <td>{index + 1}</td>
                      <td colSpan={7}>{data.label}</td>
                    </tr>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form
                      noValidate
                      validated={validate}
                      onSubmit={(event) => {
                        event.preventDefault();
                        const form = event.target;
                        let missingFields = [];
                        if (!dropdownValidated) {
                          if (frequency.frequencyType === "Daily") {
                            if (!frequency.frequencyType) missingFields.push("frequencyType");
                            if (!frequency.frequencyDate) missingFields.push("frequencyDate");
                          } else if (frequency.frequencyType === "Yearly") {
                            if (!frequency.frequencyType) missingFields.push("frequencyType");
                            if (!frequency.frequencyYear) missingFields.push("frequencyYear");
                          } else if (frequency.frequencyType === "Monthly") {
                            if (!frequency.frequencyType) missingFields.push("frequencyType");
                            if (!frequency.frequencyYear) missingFields.push("frequencyYear");
                            if (!frequency.frequencyMonth) missingFields.push("frequencyMonth");
                          } else {
                            missingFields.push("frequencyType");
                          }
                        }
                        if (form.checkValidity() && dropdownValidated) {
                          const payloadData = payload(data.value, frequency);
                          const groupPayloadData = generateGroupPayload(data.value);
                          // Validate payload for NaN values before sending
                          const validatePayload = (payload, name) => {
                            const errors = [];
                            payload.forEach((item, index) => {
                              Object.entries(item).forEach(([key, value]) => {
                                if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
                                  errors.push(`${name}[${index}].${key}: ${value}`);
                                }
                                if (typeof value === 'string' && value.toLowerCase() === 'nan') {
                                  errors.push(`${name}[${index}].${key}: "${value}"`);
                                }
                              });
                            });
                            return errors;
                          };
                          const payloadErrors = validatePayload(payloadData, 'payload');
                          const groupErrors = validatePayload(groupPayloadData, 'groupPayload');
                          if (payloadErrors.length > 0 || groupErrors.length > 0) {
                            console.error("=== NaN VALUES DETECTED ===");
                            console.error("Payload errors:", payloadErrors);
                            console.error("Group payload errors:", groupErrors);
                            console.error("This may cause the 500 error on backend");
                            console.error("========================");
                            showAlert("Invalid data detected. Please check your inputs and try again.");
                            setValidate(true);
                            return;
                          }
                          // Debug: Log what's being sent to identify 500 error cause
                          console.log("=== EMISSION DATA BEING SENT ===");
                          console.log("Payload:", payloadData);
                          console.log("Group Payload:", groupPayloadData);
                          console.log("Frequency:", frequency);
                          console.log("Data Value:", data.value);
                          console.log("===========================");
                          create({
                            payload: payloadData,
                            groupPayload: groupPayloadData,
                            showAlert,
                            setEditForm,
                            navigate,
                            update: groupsFilled?.includes(data.group),
                            formId: formid,
                            totalGroups: groupPayload.length,
                            frequency,
                            groupData: groupPayload,
                            endpoint: "emission",
                            redirectUrl: "/company/aqi",
                            pageUrl: "/company/aqi/add",
                            globalUnit,
                          });
                        } else {
                          if (missingFields.length > 0) {
                            console.log("Missing fields:", missingFields);
                          }
                          showAlert("Please fill All fields.");
                        }
                        setValidate(true);
                      }}
                    >
                      <table className="table align-middle table-bordered table-nowrap mb-0">
                        <thead>
                          <tr key={12} className="waterTabletr">
                            <th key={0}>Sr. No.</th>
                            <th key={1}>Title</th>
                            <th key={2}>Unit</th>
                            <th key={3}>Frequency</th>
                            <th key={4}>Current Value</th>
                            <th key={5}>Target Value</th>
                            <th key={6}>Comment</th>
                            <th key={8}>Attachment</th>
                          </tr>
                        </thead>
                        <TableBody
                          data={data}
                          setData={setGroupPayload}
                          frequency={frequency}
                          editForm={editForm}
                          setEditForm={setEditForm}
                          allGroups={allGroups}
                          isDeviated={isDeviated}
                          groupData={groupPayload}
                        />
                      </table>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </div>
          </Accordion>
        )}
      </div>
    </div>
  );
};

export default observer(AddAqiPage);
